<template>
  <transition
    name="accordion-fade"
    @before-enter="handleHiddenState"
    @enter="handleExpandedState"
    @before-leave="handleExpandedState"
    @leave="handleHiddenState"
  >
    <div v-if="isExpanded" ref="transitionElement">
      <div ref="resizableElement">
        <slot />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { onMounted, useTemplateRef, watch } from 'vue';

  const props = defineProps<{ isExpanded: boolean; isResizable?: boolean }>();

  const resizableElement = useTemplateRef('resizableElement');
  const transitionElement = useTemplateRef('transitionElement');
  const resizeObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      if (entry.target instanceof HTMLElement && transitionElement.value) {
        transitionElement.value.style.maxHeight = `${transitionElement.value.scrollHeight}px`;
      }
    });
  });

  function handleHiddenState(el: Element) {
    if (el instanceof HTMLElement) {
      el.style.maxHeight = '0';
    }
  }

  function handleExpandedState(el: Element) {
    if (el instanceof HTMLElement) {
      el.style.maxHeight = `${el.scrollHeight}px`;
    }
  }

  watch(resizableElement, value => {
    if (!props.isResizable) {
      return;
    }

    if (value) {
      resizeObserver.observe(value);
    } else {
      resizeObserver.disconnect();
    }
  });

  onMounted(() => {
    resizeObserver.disconnect();
  });
</script>

<style scoped>
  .accordion-fade-enter-active,
  .accordion-fade-leave-active {
    will-change: max-height, opacity;
    transition:
      max-height 0.3s ease,
      opacity 0.5s ease;
    overflow: hidden;
  }

  .accordion-fade-enter-from,
  .accordion-fade-leave-to {
    opacity: 0;
  }
</style>
